/**
 * Skeleton.jsx — Feb 2026 · reusable skeleton loader primitives.
 *
 * Every async surface in the app should mount one of these instead
 * of a bare "Loading courses…" text so cold loads feel instant on
 * 3-4G. Two building blocks:
 *   · <Skeleton /> — a single shimmering block. `w`, `h` tokens plus
 *     `rounded`, `className` for tuning per surface.
 *   · <SkeletonCard /> — a full PaperCard-shaped placeholder with a
 *     configurable line count and optional badge slot.
 *
 * The shimmer uses a diagonal linear-gradient in an infinite CSS
 * keyframe (defined in index.css) so no motion library is needed
 * and the effect renders on every browser.
 */
import React from "react";

const BASE =
  "block bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 " +
  "bg-[length:400%_100%] animate-mwq-shimmer";

/** A single shimmering block.
 *  @param {string} [props.w="100%"]  width
 *  @param {string} [props.h="1rem"]  height
 *  @param {string} [props.rounded]   Tailwind rounded utility
 *  @param {string} [props.className]
 */
export default function Skeleton({
  w = "100%",
  h = "1rem",
  rounded = "rounded-md",
  className = "",
  testid = "skeleton",
  style,
  ...rest
}) {
  return (
    <span
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid={testid}
      className={`${BASE} ${rounded} ${className}`}
      style={{ width: w, height: h, ...(style || {}) }}
      {...rest}
    />
  );
}

/** A PaperCard-shaped skeleton with a badge line + N text lines.
 *  Use in grids where the real content is a `<PaperCard>` — the
 *  layout will not jump when the real content lands. */
export function SkeletonCard({
  lines = 3,
  className = "",
  testid = "skeleton-card",
}) {
  return (
    <div
      className={`rounded-2xl border-4 border-slate-900 bg-white p-4 md:p-5 shadow-[6px_6px_0_0_rgba(15,23,42,1)] ${className}`}
      data-testid={testid}
    >
      <Skeleton w="3.5rem" h="1.75rem" rounded="rounded-full" />
      <div className="mt-3 space-y-2">
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton
            key={i}
            w={i === lines - 1 ? "60%" : "100%"}
            h="0.75rem"
          />
        ))}
      </div>
    </div>
  );
}

/** A row-shaped skeleton (avatar + two-line text) for lists. */
export function SkeletonRow({ testid = "skeleton-row" }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-slate-200 bg-white" data-testid={testid}>
      <Skeleton w="2.5rem" h="2.5rem" rounded="rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton w="60%" h="0.85rem" />
        <Skeleton w="40%" h="0.65rem" />
      </div>
    </div>
  );
}

/** Grid of N SkeletonCards — a common enough pattern to warrant a
 *  one-liner. */
export function SkeletonGrid({
  count = 6,
  cols = "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  testid = "skeleton-grid",
}) {
  return (
    <div className={`grid ${cols} gap-3 md:gap-4`} data-testid={testid}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} testid={`${testid}-card-${i}`} />
      ))}
    </div>
  );
}
